import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useAlert } from '../contexts/AlertContext'
import { getSettings } from '../lib/apiPhpAdapter'
import { parseModuleDevFlags, userBypassesModuleDev } from '../lib/moduleDevAccess'

type ModuleKey = 'fksp' | 'dovolena' | 'travel_orders'

export function ModuleDevRouteGuard({ module, children }: { module: ModuleKey; children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (authLoading || !user) return

    let cancelled = false
    ;(async () => {
      try {
        const settings = await getSettings()
        if (cancelled) return
        const flags = parseModuleDevFlags(settings)
        const restricted =
          module === 'fksp' ? flags.fksp : module === 'dovolena' ? flags.dovolena : flags.travel_orders
        if (restricted && !userBypassesModuleDev(module, user)) {
          showAlert(
            'Tento modul je dočasně uzavřen. Přístup mají ředitel, hlavní ekonom a správce IT.'
              + (module === 'travel_orders' ? ' U cestovních příkazů též zástupce ředitele.' : ''),
            'warning'
          )
          navigate('/intranet', { replace: true })
          return
        }
      } catch {
        if (!cancelled) {
          showAlert('Nepodařilo se ověřit přístup k modulu.', 'error')
          navigate('/intranet', { replace: true })
        }
        return
      }
      if (!cancelled) setReady(true)
    })()

    return () => {
      cancelled = true
    }
  }, [authLoading, user, module, navigate, showAlert])

  if (authLoading || !user) {
    return null
  }

  if (!ready) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
        Ověřování přístupu…
      </div>
    )
  }

  return <>{children}</>
}
